import { Button, Flex, Popover, Typography } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { CSSProperties, useEffect, useState } from 'react';
import './TimePickerCustom.scss';
const { Text } = Typography;
interface IPicker {
  hour: string;
  minute: string;
}

type DisableTimeType = {
  disabledHours?: number[];
  disabledMinutes?: number[];
};
type Props = {
  value?: string;
  style?: CSSProperties | undefined;
  className?: string;
  placeholder?: string;
  minuteStep?: number;
  hourStep?: number;
  isStartTime?: boolean;
  isEndTime?: boolean;
  disabledTime?: (() => DisableTimeType) | undefined;
  onChange: (time: IPicker) => void;
};

const TimePickerCustom = ({
  onChange,
  style,
  value,
  className,
  placeholder = 'Select time',
  minuteStep = 1,
  hourStep = 1,
  isEndTime,
  disabledTime
}: Props) => {
  const [selectedHour, setSelectedHour] = useState<string | undefined>(undefined);
  const [selectedMinute, setSelectedMinute] = useState<string | undefined>(undefined);

  let startHour = 0;
  let endHour = 24;

  if (isEndTime) {
    startHour = 1;
    endHour = 25;
  }

  const hourOptions: string[] = Array.from(
    { length: Math.floor((endHour - startHour) / hourStep) },
    (_, k) => (k + startHour) * hourStep
  ).map((hour) => hour.toString().padStart(2, '0'));

  const minuteOptions: string[] =
    selectedHour === '24' && isEndTime
      ? ['00']
      : Array.from({ length: Math.ceil(60 / minuteStep) }, (_, k) => k * minuteStep).map((minute) =>
          minute.toString().padStart(2, '0')
        );

  const handleHourChange = (hour: string) => {
    setSelectedHour(hour);
    if (hour === '24') {
      setSelectedMinute('00');
      onChange({ hour: '00', minute: '00' });
    } else {
      onChange({ hour: hour, minute: selectedMinute ?? '00' });
    }
  };

  const handleMinuteChange = (minute: string) => {
    setSelectedMinute(minute);

    onChange({ hour: selectedHour ?? '00', minute: minute });
  };

  useEffect(() => {
    if (value && typeof value === 'string') {
      setSelectedHour(value.split(':')[0] === '00' && isEndTime ? '24' : value.split(':')[0]);
      setSelectedMinute(value.split(':')[1]);
    }
  }, [isEndTime, value]);

  const popoverContent = (
    <Flex className={`timepicker-custom-popover ${className ?? ''}`} style={style}>
      <Flex vertical gap={5} align='center'>
        <Text strong className='timepicker-label'>
          Giờ
        </Text>
        <Flex vertical className={`hour-select`}>
          {hourOptions.map((hour) => (
            <Button
              key={hour}
              type='text'
              disabled={disabledTime ? disabledTime()?.disabledHours?.includes(Number(hour)) : false}
              className={`select-item ${hour === selectedHour ? 'selected' : ''}`}
              onClick={() => handleHourChange(hour)}
            >
              {hour}
            </Button>
          ))}
        </Flex>
      </Flex>
      <div className='divider' />
      <Flex vertical gap={5} align='center'>
        <Text strong className='timepicker-label'>
          Phút
        </Text>
        <Flex vertical className={`minute-select`}>
          {minuteOptions.map((minute) => (
            <Button
              key={minute}
              type='text'
              disabled={disabledTime ? disabledTime()?.disabledMinutes?.includes(Number(minute)) : false}
              className={`select-item ${minute === selectedMinute ? 'selected' : ''}`}
              onClick={() => handleMinuteChange(minute)}
            >
              {minute}
            </Button>
          ))}
        </Flex>
      </Flex>
    </Flex>
  );

  return (
    <Popover content={popoverContent} trigger='click' arrow={false}>
      <Button className='timepicker-custom'>
        <Flex justify='space-between' gap={8}>
          {selectedHour || selectedMinute
            ? `${selectedHour ?? '00'}:${selectedMinute ?? '00'}`
            : <Text type='secondary'>{placeholder}</Text> ?? ''}
          <Flex style={{ marginInlineStart: 'auto' }}>
            <Text type='secondary'>
              <ClockCircleOutlined />
            </Text>
          </Flex>
        </Flex>
      </Button>
    </Popover>
  );
};

export default TimePickerCustom;
